import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { HttpClient } from "@angular/common/http";
import { environment as env } from "../../../../../environments/environment.prod";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "app/common/interfaces";
import MaterialPriceListAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/materials/material-price-list/material-price-list.entity";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  FilterFn,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,
} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { AbstractService } from "app/services/abstract.service";
import { RequestOptions } from "app/common/interfaces/options.interface";

const apiUrl = env.API_MATERIAL_PRICE_LIST_URL;
const moduleName = "material-price-list";

@Injectable({
  providedIn: "root",
})
export class MaterialPriceListService extends AbstractService<
  MaterialPriceListAttributes,
  any,
  any
> {
  //TODO CREATE

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<MaterialPriceListAttributes>;

  constructor(http: HttpClient) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Obtiene lista global de estrategias de precios con paginación.
   *
   * Estrategia de búsqueda:
   * - Sin búsqueda: Un solo objeto sin filtros adicionales
   * - Con búsqueda: Dos objetos (OR lógico) - busca en 'code', 'name'
   *
   * @param options Pagination options
   * @returns Paginated data
   *
   * Search fields: code, name
   */
  findAllGlobalWithCount(
    options: IPagination<any> //MaterialPriceListAttributes
  ): Observable<IPaginationResult<any>> {

    const hasSearch = !!options.search;

    // ========== CONSTRUCCIÓN DE OBJETOS JSON ==========

    // Objeto 1: búsqueda por CODE (o vacío si no hay búsqueda)
    const searchByCodeQueryObject: FilterFn<any>[] = [
      hasSearch ? where("code", Ops.like(options.search!, "i")) : where("code", Ops.eq(""))
    ];

    // Objeto 2: búsqueda por NAME (solo si hay búsqueda)
    const searchByNameQueryObject: FilterFn<any>[] = hasSearch ? [
      orWhere("name", Ops.like(options.search!, "i"))
    ] : [];

    // ========== ENSAMBLAJE FINAL ==========

    const query = buildQuery<any>(
      // Expandimos ambos objetos en el buildQuery
      ...searchByCodeQueryObject,
      ...searchByNameQueryObject,

      // Ordenamiento y paginación
      orderBy(
        options.sort ?? "name",
        options.sortDirection === "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    this.currentFilter = query;

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByMaterial?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<any>>(API_URL);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<MaterialPriceListAttributes>,
    code: string,
    conditionClass: string,
    sequence: string
  ): Observable<IPaginationResult<MaterialPriceListAttributes>> {
    const query = buildQuery<MaterialPriceListAttributes>(
      //where("field8", Ops.like(options.search ?? "", "i")), //rango
      /*orWhere(<any>"unitMeasure.code", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"unitMeasure.name", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"priceList.code", Ops.like(options.search ?? "", "i")),
      orWhere(<any>"priceList.name", Ops.like(options.search ?? "", "i")),
      orWhere(
        <any>"materialPromotion.code",
        Ops.like(options.search ?? "", "i")
      ),
      orWhere(
        <any>"materialPromotion.name",
        Ops.like(options.search ?? "", "i")
      ), */
      where("_deleted", Ops.eq(false)),
      andAllWhere("field6", Ops.eq(code)),
      andAllWhere("conditionClass", Ops.eq(conditionClass)),
      andAllWhere("sequence", Ops.eq(sequence)),
      orderBy(
        options.sort ?? "dateEnd",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      //`api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
      `api/${moduleName}/detail?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<MaterialPriceListAttributes>>(
      API_URL
    );
  }

  getPriceListByBP(idBusinessPartner: string){
    const API_URL: string = apiUrl +`api/${moduleName}/byBusinessPartnerId/${idBusinessPartner}`;
    return this.http.get<IPaginationResult<MaterialPriceListAttributes>>(API_URL);
  }

  importFile(data: FormData) {
    const API_URL: string = apiUrl + `api/${moduleName}/upload`;
    return this.http.post(API_URL, data);
  }

  findByFilter(
    filter: CombinedFilter<MaterialPriceListAttributes>,
    options?: RequestOptions
  ) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCountByMaterial?filter=${encodeURIComponent(JSON.stringify(filter))}`;
    const headers: Record<string, any> = {};
    // see order-customer-export for more info about this header
    // ctx: stands for context
    if (options?.contextHeader) headers["x-ave-ctx"] = options.contextHeader;
    return this.http.get<IPaginationResult<MaterialPriceListAttributes>>(
      API_URL,
      { headers }
    );
  }

  getFileTemplatePriceList(): string {
    return env.PRICE_LIST_LOAD_TEMPLATE || null;
  }

  saveFileMaterilaPriceList(file: File) {
    const formData = new FormData();
    formData.append("file", file);

    const API_URL: string = apiUrl + `api/${moduleName}/uploadv2`;
    return this.http.post<IRequestResult<MaterialPriceListAttributes[]>>(
      API_URL,
      formData
    );
  }

  getMaterilaPriceListExcelData() {
    return env.LIST_PRICE_DATA;
  }

  getPriceListBySoldMaterial(soldId: string, materialId: string){
    const API_URL: string = apiUrl + `api/${moduleName}/get/pricelist/${soldId}/${materialId}`;
    return this.http.get(API_URL);
  }

  /**
   * Remove one
   * @param id Mongo id
   * @returns Data removed
   */
  removeList(id: string): Observable<IRequestResult<MaterialPriceListAttributes>> {
    const API_URL: string = apiUrl + `api/${moduleName}/list/${id}`;
    return this.http.delete<IRequestResult<MaterialPriceListAttributes>>(API_URL);
  }
}
